// Single source of truth for parsing the GA identity out of a cookie string
// (rules 23/24 — no drifting copies). Used by BOTH the client getters in
// analytics-ga4.ts (`document.cookie`) and the edge worker in beacon-edge.ts
// (the request `Cookie` header), so the server-side GA4 page_view forward reads
// EXACTLY the identity the client minted. Pure functions: parse only, never mint.

function findCookie(cookieString: string, name: string): string | undefined {
  const part = cookieString
    .split(";")
    .map((p) => p.trim())
    .find((p) => p.startsWith(`${name}=`));
  if (!part) return undefined;
  return decodeURIComponent(part.slice(name.length + 1));
}

/** GA4 client_id (`<random>.<firstSeenSec>`) from the `_ga` cookie value. */
export function parseGaClientId(cookieString: string): string | undefined {
  const value = findCookie(cookieString, "_ga");
  if (!value) return undefined;
  const parts = value.split(".");
  // `_ga` = GA1.<version>.<random>.<firstSeenSec> — the client_id is the last two.
  if (value.startsWith("GA") && parts.length >= 4) return parts.slice(-2).join(".");
  return value || undefined;
}

/**
 * GA4 ga_session_id from the `_ga_<measurement-suffix>` cookie. Handles both:
 *   GS1.1.<sessionId>.<sessionNumber>.…        (legacy)
 *   GS2.1.s<sessionId>$o<n>$g<n>$t<ts>$…        (2024+)
 */
export function parseGaSessionId(
  cookieString: string,
  measurementId: string | undefined
): string | undefined {
  if (!measurementId) return undefined;
  const value = findCookie(cookieString, `_ga_${measurementId.replace(/^G-/, "")}`);
  if (!value) return undefined;
  const v2 = value.match(/[.$]s(\d{6,})/); // GS2: …$s<sessionId>… (or ".s")
  if (v2) return v2[1];
  const parts = value.split("."); // GS1: GS1.1.<sessionId>.<sessionNumber>.…
  if (parts.length >= 3 && /^\d{6,}$/.test(parts[2])) return parts[2];
  return undefined;
}
